import React, { createContext, useContext, useState, useEffect } from 'react';
import mspService, { MSP } from '../services/mspService';

interface MSPAuthContextType {
  msp: MSP | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  impersonationData: {
    customer: any;
    impersonatedBy: { mspId: number; mspCompany: string };
  } | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  endImpersonation: () => void;
}

const MSPAuthContext = createContext<MSPAuthContextType | undefined>(undefined);

export const useMSPAuth = () => {
  const context = useContext(MSPAuthContext);
  if (!context) {
    throw new Error('useMSPAuth must be used within a MSPAuthProvider');
  }
  return context;
};

export const MSPAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [msp, setMSP] = useState<MSP | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState<any>(null);

  useEffect(() => {
    // Initialize auth state on mount
    const initAuth = () => {
      const mspData = mspService.getMSPData();
      const isAuth = mspService.isMSPAuthenticated();
      const isImp = mspService.isImpersonating();
      const impData = mspService.getImpersonationData();

      setMSP(mspData);
      setIsAuthenticated(isAuth);
      setIsImpersonating(isImp);
      setImpersonationData(impData);

      // Initialize API headers
      mspService.initializeAuth();
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await mspService.login(email, password);
    if (response.token) {
      setMSP(response.msp);
      setIsAuthenticated(true);
    }
  };

  const logout = async () => {
    await mspService.logout();
    setMSP(null);
    setIsAuthenticated(false);
    setIsImpersonating(false);
    setImpersonationData(null);
  };

  const endImpersonation = () => {
    mspService.endImpersonation();
    setIsImpersonating(false);
    setImpersonationData(null);
  };

  const value = {
    msp,
    isAuthenticated,
    isImpersonating,
    impersonationData,
    login,
    logout,
    endImpersonation
  };

  return (
    <MSPAuthContext.Provider value={value}>
      {children}
    </MSPAuthContext.Provider>
  );
};